const cities = [
  { name: "Parkland", zips: "33067, 33073, 33076" },
  { name: "Coral Springs", zips: "33065, 33071, 33076" },
  { name: "Boca Raton", zips: "33428, 33431, 33432" },
  { name: "Deerfield Beach", zips: "33441, 33442" },
  { name: "Coconut Creek", zips: "33063, 33066, 33073" },
  { name: "Pompano Beach", zips: "33060, 33062, 33064" },
  { name: "Tamarac", zips: "33309, 33319, 33321" },
  { name: "Margate", zips: "33063, 33068" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home or work in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function ParklandLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Parkland &amp; Northern Broward County
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Parkland, Coral Springs, Coconut Creek, or anywhere in northern Broward County, Fast Passport Center makes expedited passport processing simple — no drive to Miami, no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Parkland is unlike any other city in South Florida. Incorporated in 1963 as a master-planned community with a singular vision — to preserve the area&apos;s rural, equestrian character — Parkland has maintained strict zoning codes that require acre-plus lots, limit commercial development, and protect the winding horse trails that give the city its identity. While neighboring Coral Springs and Boca Raton grew into dense suburban centers, Parkland deliberately chose a different path: wide open spaces, mature oak canopies, and a lifestyle centered on family, nature, and community.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The result is one of the most affluent and family-oriented communities in Broward County. Parkland is home to some of the top-rated public schools in Florida, including Marjory Stoneman Douglas High School, which consistently ranks among the best comprehensive high schools in the state. The city&apos;s demographics skew heavily toward families with children — which means a disproportionate number of five-year child passports cycling through expiration at any given time. For Parkland parents, the discovery that a child&apos;s passport expired three months ago often happens during spring break planning or summer vacation booking.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Parkland&apos;s equestrian heritage is not just historical — it is actively maintained through the city&apos;s extensive trail system, horse-friendly zoning, and events like the Parkland Farmers&apos; Market and equestrian competitions. The Parkland Golf &amp; Country Club and Pine Trails Park serve as community anchors, drawing residents together for recreation, festivals, and seasonal celebrations. Despite its rural aesthetic, Parkland sits at a remarkably convenient crossroads: roughly equidistant from Fort Lauderdale-Hollywood International Airport (FLL) to the south and Palm Beach International Airport (PBI) to the north — both within 25–30 minutes via the Sawgrass Expressway and I-95.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            This dual-airport access is unique among South Florida suburbs. Parkland residents can choose FLL for its extensive international route network — particularly to Latin America, the Caribbean, and Europe — or PBI for its efficiency, shorter security lines, and direct service to seasonal destinations. The city also sits within easy driving distance of Port Everglades in Fort Lauderdale (approximately 30 minutes south) and the Port of Miami (approximately 45 minutes south), making cruise departures a regular part of the Parkland travel diet.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Parkland&apos;s travel culture reflects its family-first demographics. Spring break trips to the Caribbean, summer vacations in Europe, and college-campus visits across the United States are staples of the Parkland family calendar. The city also has a significant Jewish community that travels regularly to Israel for family visits, religious pilgrimage, and cultural tourism — trips that require current passports and, in many cases, additional visa documentation. Business travel is present but less dominant than in downtown Fort Lauderdale or Miami; Parkland&apos;s professional class tends to work in nearby corporate centers, medical offices, or commute to Fort Lauderdale and Boca Raton.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            What unites all of these travelers is the same reality: Parkland residents live in a community where international travel is normal, expected, and often planned on tight timelines. When a passport expires or gets damaged days before a planned departure, standard government processing is simply not an option. Fast Passport Center has processed expedited passports for Parkland travelers from Heron Bay, Parkland Golf &amp; Country Club, Pine Tree Estates, and every neighborhood in between. Our courier-based expedited service means you start online from your home on a two-acre lot — and your passport arrives within days, not months.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Parkland — approximately 20 miles north via I-95 or the Sawgrass Expressway
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout northern Broward County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or office in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our Delray Beach office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities &amp; Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in northern Broward County — start online, ship overnight, and we handle the rest. No need to drive to Miami.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Coral Springs", href: "/cities/coral-springs" },
                  { name: "Boca Raton", href: "/cities/boca-raton" },
                  { name: "Weston", href: "/cities/weston" },
                  { name: "West Palm Beach", href: "/cities/west-palm-beach" },
                  { name: "Fort Lauderdale", href: "/cities/fort-lauderdale" },
                  { name: "Aventura", href: "/cities/aventura" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}